'use client';

import { useSupabaseAuth } from '@/components/auth/SupabaseAuthProvider';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useRouter } from '@/i18n/routing';
import { Link } from '@/i18n/routing';
import {
  AlertCircle,
  BarChart3,
  Building2,
  Check,
  LogOut,
  Settings,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { UserMenuHeader } from './UserMenuHeader';

interface UserMenuContentProps {
  userProfileUrl?: '/user-profile';
}

/**
 * Renders the inner content of the user dropdown menu:
 * user info header, usage/settings links, and sign-out action.
 *
 * Use inside a DropdownMenuContent wrapper — this component does NOT
 * include DropdownMenu or DropdownMenuContent, so callers control
 * the trigger and positioning.
 */
export function UserMenuContent({
  userProfileUrl = '/user-profile',
}: UserMenuContentProps) {
  const { signOut } = useSupabaseAuth();
  const {
    user,
    userEmail,
    isInOrg,
    orgName,
    orgId,
    isOrgAdmin,
    hasActiveSubscription,
  } = useAuthUser();
  const router = useRouter();
  const t = useTranslations('UserMenu');

  const displayName = user?.fullName || userEmail || t('defaultName');

  const handleSignOut = async () => {
    await signOut();
    router.push('/');
  };

  return (
    <>
      <UserMenuHeader displayName={displayName} email={userEmail} />
      {/* Org context — shown when user is in an org */}
      {isInOrg ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className='cursor-pointer'>
            <Link
              href={
                !hasActiveSubscription && isOrgAdmin && orgId
                  ? (`/org-onboarding/${orgId}` as '/organization')
                  : '/organization'
              }
            >
              <Building2 className='mr-2 h-4 w-4' />
              <span className='flex-1 truncate'>{orgName}</span>
              {hasActiveSubscription ? (
                <span className='ml-1 flex items-center gap-1 text-[10px] text-status-success-text'>
                  <Check className='h-3 w-3' />
                  {t('orgActive')}
                </span>
              ) : (
                <span className='ml-1 flex items-center gap-1 text-[10px] text-status-warning'>
                  <AlertCircle className='h-3 w-3' />
                  {isOrgAdmin ? t('completeOrgSetup') : t('orgInactive')}
                </span>
              )}
            </Link>
          </DropdownMenuItem>
        </>
      ) : null}
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild className='cursor-pointer'>
        <Link href={'/user-profile/usage' as '/user-profile'}>
          <BarChart3 className='mr-2 h-4 w-4' />
          {t('usage')}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className='cursor-pointer'>
        <Link href={userProfileUrl}>
          <Settings className='mr-2 h-4 w-4' />
          {t('settings')}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        aria-label='sign-out'
        onClick={handleSignOut}
        className='cursor-pointer'
      >
        <LogOut className='mr-2 h-4 w-4' />
        {t('signOut')}
      </DropdownMenuItem>
    </>
  );
}
